"use client";

/**
 * Accordion View
 * Renders schedule items grouped by date in an expandable accordion layout.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { FetchButton } from "@/fetch-ui/FetchButton";
import type { ScheduleItemRow, ScheduleLabelRow } from "../../../../_logic/types/db-types";
import { ScheduleItemDisplay } from "./ScheduleItemDisplay";
import { DateWithWeekday } from "./DateWithWeekday";
import { ItemDeleteButton } from "./ItemDeleteButton";
import { getFirstLabelColor } from "./label-utils";
import {
  getScheduleItemRegistrationState,
  type ViewerRegistrationKeys,
} from "../../../../_logic/utils/schedule-registration-match";

interface AccordionViewProps {
  dateGroups: { date: string | null; items: ScheduleItemRow[] }[];
  labels: ScheduleLabelRow[];
  /** Pre-computed label lookup map (computed once in ScheduleRenderer) */
  labelMap: Map<number, ScheduleLabelRow>;
  primaryColor: string;
  onDelete?: (item: ScheduleItemRow) => void;
  /** When provided, items become clickable (used in public view for detail modal) */
  onItemClick?: (item: ScheduleItemRow) => void;
  /** Page-wide IANA timezone for rendering times. NULL = floating time. */
  timeZone: string | null;
  isPublic?: boolean;
  /** Viewer's confirmed registrations. `null` = no logged-in attendee. */
  viewerRegistrations: ViewerRegistrationKeys | null;
}

/** Renders schedule items in collapsible accordion sections grouped by date */
export function AccordionView({
  dateGroups,
  labels,
  labelMap,
  primaryColor,
  onDelete,
  onItemClick,
  timeZone,
  isPublic = false,
  viewerRegistrations,
}: AccordionViewProps) {
  const [expandedDates, setExpandedDates] = useState<Set<string | null>>(() => new Set(dateGroups.map((g) => g.date)));

  // Effects — expand newly added date groups
  useEffect(() => {
    setExpandedDates((prev) => {
      const currentDates = new Set(dateGroups.map((g) => g.date));
      const newDates = [...currentDates].filter((d) => !prev.has(d));
      if (newDates.length === 0) return prev;
      return new Set([...prev, ...newDates]);
    });
  }, [dateGroups]);

  const toggleDate = useCallback((date: string | null) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) {
        next.delete(date);
      } else {
        next.add(date);
      }
      return next;
    });
  }, []);

  const allExpanded = useMemo(() => dateGroups.every((g) => expandedDates.has(g.date)), [dateGroups, expandedDates]);

  const toggleAll = useCallback(() => {
    if (allExpanded) {
      setExpandedDates(new Set());
    } else {
      setExpandedDates(new Set(dateGroups.map((g) => g.date)));
    }
  }, [allExpanded, dateGroups]);

  return (
    <div className="flex flex-col gap-3">
      {dateGroups.length > 1 && (
        <div className="flex justify-end">
          <FetchButton
            variant="ghost"
            color="neutral"
            size="xs"
            className="touch-manipulation"
            leftIcon={
              allExpanded ? <ChevronsDownUp className="h-3.5 w-3.5" /> : <ChevronsUpDown className="h-3.5 w-3.5" />
            }
            onClick={toggleAll}
          >
            {allExpanded ? "Collapse All" : "Expand All"}
          </FetchButton>
        </div>
      )}
      {dateGroups.map((group) => (
        <div key={group.date ?? "no-date"} className="overflow-hidden rounded-lg border border-gray-200">
          {/* Header */}
          <button
            type="button"
            onClick={() => toggleDate(group.date)}
            className="flex w-full touch-manipulation items-center justify-between bg-white px-6 py-4 text-left"
            aria-expanded={expandedDates.has(group.date)}
          >
            <span className="text-lg font-semibold text-gray-900">
              {group.date ? <DateWithWeekday date={group.date} /> : "Unscheduled"}
            </span>
            <ChevronDown
              className={`h-5 w-5 text-gray-400 transition-transform ${
                expandedDates.has(group.date) ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Items */}
          {expandedDates.has(group.date) && (
            <div className="flex flex-col divide-y divide-gray-100">
              {group.items.map((item) => {
                const borderColor = getFirstLabelColor(item, labelMap);
                const registrationState = getScheduleItemRegistrationState(item, viewerRegistrations);
                const registrationRing =
                  registrationState === "registered"
                    ? "rounded-md ring-1 ring-emerald-500/40"
                    : registrationState === "waitlisted"
                      ? "rounded-md ring-1 ring-amber-500/40"
                      : "";
                return (
                  <div
                    key={item.id}
                    className={`group/item flex items-start justify-between gap-3 border-l-4 px-6 py-4 ${onItemClick ? "cursor-pointer touch-manipulation transition-colors hover:bg-gray-50" : ""} ${registrationRing}`}
                    style={{ borderLeftColor: borderColor ?? primaryColor }}
                    onClick={onItemClick ? () => onItemClick(item) : undefined}
                    role={onItemClick ? "button" : undefined}
                    tabIndex={onItemClick ? 0 : undefined}
                    onKeyDown={
                      onItemClick
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onItemClick(item);
                            }
                          }
                        : undefined
                    }
                  >
                    <ScheduleItemDisplay
                      item={item}
                      labels={labels}
                      primaryColor={primaryColor}
                      showDate={group.date === null}
                      timeZone={timeZone}
                      isPublic={isPublic}
                      viewerRegistrations={viewerRegistrations}
                    />
                    {onDelete && <ItemDeleteButton item={item} onDelete={onDelete} />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
